import React, { useState, useCallback } from 'react';
import Loader from '../../components/common/Loader';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import { formatCurrency, formatDate } from '../../utils/helpers';
import axiosInstance from '../../api/axios';

// ─── Result bucket card ───────────────────────────────────────────────────────
const BucketCard = ({ label, count, amount, color, icon, children }) => {
  const [open, setOpen] = useState(false);
  const colors = {
    green:  { bg: 'bg-green-50',  border: 'border-green-200', text: 'text-green-700',  badge: 'bg-green-100 text-green-800'  },
    red:    { bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-700',    badge: 'bg-red-100 text-red-800'      },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200',text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800'},
    gray:   { bg: 'bg-gray-50',   border: 'border-gray-200',  text: 'text-gray-700',   badge: 'bg-gray-100 text-gray-800'    },
    blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-800'    },
  };
  const c = colors[color] || colors.gray;

  return (
    <div className={`rounded-lg border ${c.border} ${c.bg} overflow-hidden`}>
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => count > 0 && setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{icon}</span>
          <div>
            <p className={`text-sm font-semibold ${c.text}`}>{label}</p>
            {amount !== undefined && (
              <p className="text-xs text-gray-500 mt-0.5">{formatCurrency(amount)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold ${c.badge}`}>{count}</span>
          {count > 0 && (
            <svg className={`w-4 h-4 ${c.text} transition-transform ${open ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {open && count > 0 && (
        <div className="border-t border-gray-200 overflow-x-auto">
          <table className="min-w-full text-xs">
            <tbody className="divide-y divide-gray-100 bg-white">
              {children}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TRow = ({ cells }) => (
  <tr className="hover:bg-gray-50">
    {cells.map((cell, i) => (
      <td key={i} className="px-4 py-2 whitespace-nowrap text-gray-700">{cell}</td>
    ))}
  </tr>
);

// ─── Main component ───────────────────────────────────────────────────────────
const ReconciliationTab = ({ selectedContest, contests }) => {
  const { error: showError, success: showSuccess } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  const [preview,   setPreview]   = useState(null);
  const [results,   setResults]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [running,   setRunning]   = useState(false);
  const [fromDate,  setFromDate]  = useState('');
  const [toDate,    setToDate]    = useState('');

  const contestData = contests?.find(c => c.id === selectedContest);

  // ── Preview ────────────────────────────────────────────────────────────────
  const loadPreview = useCallback(async () => {
    if (!selectedContest) return;
    try {
      setLoading(true);
      setPreview(null);
      const res = await axiosInstance.get(`/payments/reconcile/${selectedContest}/preview`);
      if (res.data?.success) setPreview(res.data.data);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [selectedContest]);

  // ── Run ────────────────────────────────────────────────────────────────────
  const runReconciliation = async () => {
    if (!selectedContest) return;
    try {
      setRunning(true);
      setResults(null);
      const res = await axiosInstance.post(`/payments/reconcile/${selectedContest}`, {
        ...(fromDate && { from: fromDate }),
        ...(toDate   && { to:   toDate   }),
      });
      if (res.data?.success) {
        setResults(res.data.data);
        const fixed = res.data.data.summary?.totalFixed || 0;
        const rev   = res.data.data.summary?.revenueRecovered || 0;
        if (fixed > 0) {
          showSuccess(`Reconciliation complete — ${fixed} payment(s) fixed, GHS ${rev} recovered`);
        } else {
          showSuccess('Reconciliation complete — everything is in order');
        }
        // Refresh preview
        loadPreview();
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Reconciliation failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* ── Header + Contest info ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Payment Reconciliation</h3>
            <p className="text-sm text-gray-500 mt-1">
              Compares your database against <strong>Paystack</strong> and <strong>AppsNmobile</strong>.
              Any payment confirmed on their end but still <em>PENDING</em> in your DB will be auto-completed.
            </p>
            {contestData && (
              <p className="text-xs text-blue-600 font-medium mt-2">
                Contest: {contestData.title}
              </p>
            )}
          </div>

          {/* Load preview button */}
          <button
            onClick={loadPreview}
            disabled={loading || !selectedContest}
            className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center"
          >
            {loading
              ? <><span className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mr-2" />Loading…</>
              : <><svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>Load Preview</>
            }
          </button>
        </div>

        {/* ── Preview cards ──────────────────────────────────────────────── */}
        {preview && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Pending Paystack',    value: preview.pendingPayments?.paystack,    color: 'yellow' },
              { label: 'Pending AppsNmobile', value: preview.pendingPayments?.appsNmobile, color: 'orange' },
              { label: 'Completed Payments',  value: preview.completedPayments,            color: 'green'  },
              { label: 'Manual Votes',        value: preview.manualVotes?.totalVotes,       color: 'purple' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-2xl font-bold text-${color}-600`}>{value ?? '—'}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Date range + Run ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Date Range (optional — defaults to contest start → now)</h4>
        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="datetime-local"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="datetime-local"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={runReconciliation}
            disabled={running || !selectedContest}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
          >
            {running
              ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Running…</>
              : <><svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>Run Reconciliation</>
            }
          </button>
        </div>

        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-md p-3 text-xs text-yellow-800">
          <strong>What this does:</strong> Checks every <em>PENDING</em> Paystack and AppsNmobile payment.
          If the payment gateway confirms the money was received, the vote is automatically counted.
          Ghost votes (counted without payment) are flagged for your review — they are <strong>not</strong> deleted automatically.
        </div>
      </div>

      {/* ── Results ───────────────────────────────────────────────────────── */}
      {results && (
        <div className="space-y-4">

          {/* Summary bar */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-900">
                Results — {results.contest?.title}
              </h4>
              <span className="text-xs text-gray-400">Completed in {results.duration}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Payments Fixed',      value: results.summary?.totalFixed,            color: 'green'  },
                { label: 'Revenue Recovered',   value: formatCurrency(results.summary?.revenueRecovered || 0), color: 'green', raw: true },
                { label: 'Ghost Votes Flagged', value: results.summary?.totalGhosts,           color: 'red'    },
                { label: 'Range Checked',       value: `${results.rangeChecked?.from?.substring(0,10)} → ${results.rangeChecked?.to?.substring(0,10)}`, color: 'blue', raw: true },
              ].map(({ label, value, color, raw }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className={`${raw ? 'text-sm' : 'text-2xl'} font-bold text-${color}-600`}>{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Paystack results ─────────────────────────────────────────── */}
          {results.paystack && !results.paystack.error && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                Paystack
              </h4>
              <div className="space-y-2">

                <BucketCard label="Fixed — paid on Paystack, was PENDING in DB" count={results.paystack.counts?.fixed} color="green" icon="✅"
                  amount={results.paystack.fixedInDB?.reduce((s, p) => s + (p.amount || 0), 0)}>
                  {results.paystack.fixedInDB?.map((p, i) => (
                    <TRow key={i} cells={[p.reference, p.contestant || '—', formatCurrency(p.amount), `${p.voteCount} vote(s)`]} />
                  ))}
                </BucketCard>

                <BucketCard label="Matched — both sides agree" count={results.paystack.counts?.matched} color="blue" icon="🤝">
                  {results.paystack.matched?.map((p, i) => (
                    <TRow key={i} cells={[p.reference, p.contestant || '—', formatCurrency(p.amount)]} />
                  ))}
                </BucketCard>

                <BucketCard label="Ghost votes — DB says COMPLETED but Paystack has no record 🚨" count={results.paystack.counts?.ghosts} color="red" icon="👻">
                  {results.paystack.ghostInDB?.map((p, i) => (
                    <TRow key={i} cells={[
                      p.reference,
                      p.contestant || p.paystackData?.email || '—',
                      formatCurrency(p.amount),
                      p.note || 'DB completed, no Paystack success record',
                    ]} />
                  ))}
                </BucketCard>

                <BucketCard label="Skipped — PENDING in DB, no Paystack record (likely USSD)" count={results.paystack.counts?.skipped} color="gray" icon="⏭️">
                  {results.paystack.skipped?.map((p, i) => (
                    <TRow key={i} cells={[p.reference, p.reason]} />
                  ))}
                </BucketCard>

              </div>
            </div>
          )}

          {results.paystack?.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              <strong>Paystack check failed:</strong> {results.paystack.error}
            </div>
          )}

          {/* ── AppsNmobile results ──────────────────────────────────────── */}
          {results.appsNmobile && !results.appsNmobile.error && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                AppsNmobile (USSD / MoMo)
              </h4>
              <div className="space-y-2">

                <BucketCard label="Fixed — confirmed by AppsNmobile, was PENDING in DB" count={results.appsNmobile.counts?.fixed} color="green" icon="✅"
                  amount={results.appsNmobile.fixedInDB?.reduce((s, p) => s + (p.amount || 0), 0)}>
                  {results.appsNmobile.fixedInDB?.map((p, i) => (
                    <TRow key={i} cells={[p.reference, p.contestant || '—', formatCurrency(p.amount), p.network || '—', `${p.voteCount} vote(s)`]} />
                  ))}
                </BucketCard>

                <BucketCard label="Still pending — payment not yet confirmed" count={results.appsNmobile.counts?.stillPending} color="yellow" icon="⏳">
                  {results.appsNmobile.stillPending?.map((p, i) => (
                    <TRow key={i} cells={[p.reference, p.contestant || '—', formatCurrency(p.amount), p.status]} />
                  ))}
                </BucketCard>

                <BucketCard label="Failed — payment cancelled or declined" count={results.appsNmobile.counts?.failed} color="red" icon="❌">
                  {results.appsNmobile.failed?.map((p, i) => (
                    <TRow key={i} cells={[p.reference, p.contestant || '—', formatCurrency(p.amount), p.status]} />
                  ))}
                </BucketCard>

                <BucketCard label="Errors — could not check status" count={results.appsNmobile.counts?.errors} color="gray" icon="⚠️">
                  {results.appsNmobile.errors?.map((p, i) => (
                    <TRow key={i} cells={[p.reference, p.reason]} />
                  ))}
                </BucketCard>

              </div>
            </div>
          )}

          {results.appsNmobile?.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              <strong>AppsNmobile check failed:</strong> {results.appsNmobile.error}
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default ReconciliationTab;